<template>
<div>
    <v-app-bar
      v-if="showAppBar"
      class="nav-app-bar"
      :class="{
        'nav-app-bar--phone': phoneSmarty,
        'nav-app-bar--standalone': phoneSmarty && standalone,
      }"
      :color="phoneSmarty ? 'white' : undefined"
      :fixed="pinAppBar"
      :elevate-on-scroll="!pinAppBar"
      height="auto"
    >
        <!--
          Phone: ae mark + wordmark + SRM, then the avatar menu.
          Desktop: sidebar hide/show chevron + cloud logo + email. No hamburger.
        -->
        <div v-if="phoneSmarty" class="nav-bar-row nav-bar-row--phone">
            <div class="nav-brand-phone">
                <!--
                  PT-2: the ae mark returns to Matrix. Add to Home Screen
                  lives on the avatar menu.
                -->
                <RocketLauncherButton
                  variant="mark"
                  @activate="goToMatrix"
                />
                <div
                  class="nav-brand-text"
                  role="link"
                  tabindex="0"
                  title="Back to Matrix"
                  @click="goToMatrix"
                  @keydown.enter.prevent="goToMatrix"
                >
                  <span class="nav-wordmark">
                    <span class="nav-wordmark-account">account</span><span class="nav-wordmark-exec">exec</span>
                  </span>
                  <span class="nav-srm-phone">Sales Relationship Management (SRM)</span>
                </div>
            </div>
            <v-spacer></v-spacer>
            <div ref="avatarWrap" class="nav-avatar-wrap">
              <button
                type="button"
                class="nav-avatar-btn"
                aria-haspopup="menu"
                :aria-expanded="avatarOpen ? 'true' : 'false'"
                aria-label="Account menu"
                @click="toggleAvatar"
              >
                <span class="nav-avatar" aria-hidden="true">{{ avatarInitials }}</span>
                <v-icon class="nav-avatar-chevron" small>mdi-chevron-down</v-icon>
              </button>
              <div
                v-if="avatarOpen"
                class="nav-avatar-menu"
                role="menu"
              >
                <div class="nav-avatar-who">
                  <div class="nav-avatar-name">{{ avatarName }}</div>
                  <div v-if="avatarTitle" class="nav-avatar-title">{{ avatarTitle }}</div>
                </div>
                <button type="button" role="menuitem" @click="goProfile">Profile</button>
                <button type="button" role="menuitem" @click="comingSoon('Preferences')">Preferences</button>
                <button type="button" role="menuitem" @click="comingSoon('Help')">Help</button>
                <button
                  v-if="showHomeScreenMenu"
                  type="button"
                  role="menuitem"
                  @click="openHomeScreenFromMenu"
                >Add to Home Screen</button>
                <button type="button" class="nav-avatar-signout" role="menuitem" @click="goLogout">Sign out</button>
              </div>
            </div>
        </div>

        <div v-else class="nav-bar-row nav-bar-row--desktop">
            <button
              type="button"
              class="nav-rail-toggle"
              :class="{ 'is-collapsed': sidebarCollapsed }"
              :title="sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'"
              :aria-label="sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'"
              :aria-expanded="sidebarCollapsed ? 'false' : 'true'"
              aria-controls="dux-desktop-rail"
              @click="toggleDuxRail"
            >
              <v-icon>{{ sidebarCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
              <span class="nav-rail-toggle-label">{{ sidebarCollapsed ? 'Show sidebar' : 'Hide' }}</span>
            </button>
            <div
              class="nav-brand-desktop"
              role="link"
              tabindex="0"
              title="Back to Matrix"
              @click="goToMatrix"
              @keydown.enter.prevent="goToMatrix"
            >
                <img
                  class="nav-cloud-logo"
                  alt="accountexec"
                  src="/logo_with_tm.png"
                />
                <span class="nav-srm-desktop">Sales Relationship Management (SRM)</span>
            </div>
            <v-spacer></v-spacer>
            <div class="nav-user" :title="userName">
                <span class="nav-user-email">{{ userName }}</span>
            </div>
        </div>
    </v-app-bar>

    <RocketLauncherSheet v-if="mountRocketSheet" />
</div>
</template>


<script>
import axios from 'axios'
import RocketLauncherButton from '../../../mobile/RocketLauncherButton.vue'
import RocketLauncherSheet from '../../../mobile/RocketLauncherSheet.vue'
import {
  installPath,
  isHomeScreenEnabled,
  isStandalone,
  subscribeInstallState,
} from '../../../mobile/homeScreenInstall'
import {
  openRocketLauncherSheet,
  shouldMountRocketLauncherSheet,
} from '../../../mobile/rocketLauncherSheetBus'
import {
  isPhoneSmartyViewport,
  watchPhoneSmartyViewport,
} from '../../../mobile/phoneSmartyViewport'
import {
  getSidebarCollapsed,
  subscribeSidebarCollapsed,
  toggleSidebarCollapsed,
} from '../../../desktop/desktopSidebarStorage'

export default {
    name: "UniversalNav",
    data() {
        return {
            group: null,
            phoneSmarty: isPhoneSmartyViewport(),
            sidebarCollapsed: getSidebarCollapsed(),
            standalone: isStandalone(),
            installTick: 0,
            unwatchViewport: null,
            unsubInstall: null,
            unsubRail: null,
            avatarOpen: false,
            avatarFirst: '',
            avatarLast: '',
            avatarTitle: '',
        };
    },
    mounted () {
        this.unwatchViewport = watchPhoneSmartyViewport((isPhone) => {
            this.phoneSmarty = isPhone
            this.standalone = isStandalone()
            if (isPhone) this.loadPhoneIdentity()
        })
        this.unsubInstall = subscribeInstallState(() => {
            this.installTick += 1
            this.standalone = isStandalone()
        })
        this.unsubRail = subscribeSidebarCollapsed((collapsed) => {
            this.sidebarCollapsed = collapsed
        })
        if (this.standalone) {
          document.documentElement.classList.add('ae-standalone')
        }
        this._onAvatarDoc = (event) => this.onAvatarDoc(event)
        this._onAvatarKey = (event) => this.onAvatarKey(event)
        document.addEventListener('pointerdown', this._onAvatarDoc)
        document.addEventListener('keydown', this._onAvatarKey)
        this.loadPhoneIdentity()
    },
    beforeDestroy () {
        if (typeof this.unwatchViewport === 'function') this.unwatchViewport()
        if (typeof this.unsubInstall === 'function') this.unsubInstall()
        if (typeof this.unsubRail === 'function') this.unsubRail()
        document.removeEventListener('pointerdown', this._onAvatarDoc)
        document.removeEventListener('keydown', this._onAvatarKey)
    },
    methods: {
        goToMatrix () {
            this.avatarOpen = false
            this.$router.push({ name: 'landing', query: { tab: 'matrix' } }).catch(() => {})
        },
        toggleDuxRail () {
            toggleSidebarCollapsed()
        },
        toggleAvatar () {
            this.avatarOpen = !this.avatarOpen
        },
        onAvatarDoc (event) {
            if (!this.avatarOpen) return
            const root = this.$refs.avatarWrap
            if (root && root.contains(event.target)) return
            this.avatarOpen = false
        },
        onAvatarKey (event) {
            if (event.key === 'Escape') this.avatarOpen = false
        },
        goProfile () {
            this.avatarOpen = false
            this.$router.push({ name: 'profile' }).catch(() => {})
        },
        goLogout () {
            this.avatarOpen = false
            this.$router.push({ name: 'logout' }).catch(() => {})
        },
        comingSoon (label) {
            this.avatarOpen = false
            this.$toast.info(label + ' is coming soon.')
        },
        openHomeScreenFromMenu () {
            this.avatarOpen = false
            openRocketLauncherSheet()
        },
        loadPhoneIdentity () {
            if (!this.phoneSmarty || !this.showAppBar) return
            const id = this.$store.state.id
            const token = this.$store.state.userToken
            if (!id || !token) return
            axios({
                method: 'get',
                url: `${this.$store.getters.baseURL}/api/user/${id}`,
                headers: { Authorization: `Token ${token}` },
            }).then((res) => {
                const data = res.data || {}
                this.avatarFirst = data.first_name || ''
                this.avatarLast = data.last_name || ''
                this.avatarTitle = data.title || ''
            }).catch(() => {})
        },
    },
    computed: {
        loggedIn () {
            return this.$store.getters.isLoggedIn
        },
        acceptedEula () {
            return this.$store.getters.hasAcceptedEula
        },
        userName () {
            return this.$store.getters.userName
        },
        avatarInitials () {
            const f = (this.avatarFirst || '').charAt(0)
            const l = (this.avatarLast || '').charAt(0)
            return (f + l).toUpperCase() || '?'
        },
        avatarName () {
            const name = `${this.avatarFirst || ''} ${this.avatarLast || ''}`.trim()
            return name || this.userName
        },
        /**
         * Login/register must never show the previous session's email.
         * Router persistLogin can restore Vuex on the public page for one tick.
         */
        isPublicRoute () {
            const name = this.$route && this.$route.name
            return name === 'home' || name === 'register' || name === 'logout' || name === 'forgot' || name === 'reset' || name === 'kickoff'
        },
        showAppBar () {
            return this.loggedIn && this.acceptedEula && !this.isPublicRoute
        },
        /** Manage Gold: keep logo / banner visible while scrubbing the list. */
        pinAppBar () {
            return this.$route && this.$route.name === 'companyConsole'
        },
        /** Avatar item until already installed / standalone. */
        showHomeScreenMenu () {
            void this.installTick
            if (!this.phoneSmarty) return false
            if (!isHomeScreenEnabled()) return false
            if (isStandalone()) return false
            const path = installPath()
            return path !== 'installed' && path !== 'disabled'
        },
        /** Phone Smarty, or localhost / ?rl_sheet=1 so local smoke can show the sheet. */
        mountRocketSheet () {
            return shouldMountRocketLauncherSheet(this.phoneSmarty)
        },
    },
    watch: {
        isPublicRoute (isPublic) {
            if (isPublic) this.avatarOpen = false
        },
        showAppBar (show) {
            if (show) this.loadPhoneIdentity()
        },
    },
    components: { RocketLauncherButton, RocketLauncherSheet }
}
</script>
<style scoped>
.nav-app-bar >>> .v-toolbar__content {
  display: flex;
  align-items: center;
  height: auto !important;
  min-height: 56px;
  padding: 0 8px;
  overflow: visible;
}

.nav-app-bar--phone >>> .v-toolbar__content {
  display: block;
  min-height: 0;
  padding: 6px 4px 6px 10px;
  background: #fff;
  overflow: visible;
}

.nav-app-bar--phone {
  background-color: #fff !important;
  /*
   * Matrix does not pin this bar (:fixed is Manage Gold only), so the
   * z-index on .v-app-bar--fixed never applies here. The phone shell is
   * position:fixed at z-index 5 and starts at the bar's bottom edge.
   * The avatar menu hangs into that region. A stacking context on the
   * bar keeps the whole menu above the shell, and below phone sheets
   * (those start at 40).
   */
  position: relative;
  z-index: 30;
  overflow: visible;
}

/* RL-5: Home Screen tile. Respect notch / status bar */
.nav-app-bar--standalone.nav-app-bar--phone >>> .v-toolbar__content {
  padding-top: max(6px, env(safe-area-inset-top, 0px));
}

.nav-app-bar.v-app-bar--fixed {
  /* Above Rocket Launcher dim so brand lockup stays readable while sheet is open */
  z-index: 4010;
}

.nav-bar-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 4px;
}

/* Phone Smarty banner */
.nav-menu {
  flex: 0 0 auto;
  margin-right: -4px;
}

.nav-brand-phone {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
}

.nav-ae-mark {
  display: block;
  width: 59px;
  height: 59px;
  flex-shrink: 0;
  object-fit: contain;
}

.nav-brand-text {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  height: 59px;
  min-width: 0;
  gap: 0;
  transform: translateY(-6px);
  cursor: pointer;
}

.nav-wordmark {
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.nav-wordmark-account {
  color: #111827;
}

.nav-wordmark-exec {
  color: #00b0f0;
}

.nav-srm-phone {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.15;
  color: #111827;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
  max-width: none;
}

/* Desktop banner (pre Phone Smarty lockup) */
.nav-rail-toggle {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 40px;
  height: 40px;
  margin: 0 4px 0 -4px;
  padding: 0 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #12202f;
  cursor: pointer;
  font-family: inherit;
}
.nav-rail-toggle:hover {
  background: #f1f5f9;
}
.nav-rail-toggle:focus-visible {
  outline: 2px solid #00b0f0;
  outline-offset: 2px;
}
.nav-rail-toggle-label {
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  color: inherit;
}
.nav-rail-toggle.is-collapsed .nav-rail-toggle-label {
  font-size: 13px;
}

.nav-brand-desktop {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: visible;
  cursor: pointer;
}

.nav-cloud-logo {
  display: block;
  height: 44px;
  width: auto;
  flex-shrink: 0;
}

.nav-srm-desktop {
  font-family: 'Arial Rounded MT Bold', 'Arial Rounded MT', Arial, sans-serif;
  font-weight: normal;
  font-size: 20.7px;
  line-height: 1;
  color: #00B0F0;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: visible;
  transform: translateY(2px);
}

.nav-user {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;
  margin-left: 8px;
  min-width: 0;
}

.nav-user-email {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: #111827;
  white-space: nowrap;
}

.nav-drawer-user {
  min-height: 72px;
}

.nav-drawer-brand {
  font-size: 16px !important;
  font-weight: 800 !important;
  letter-spacing: -0.02em;
  color: #111827;
}

.nav-drawer-email {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #4b5563 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  opacity: 1 !important;
}

.nav-avatar-wrap {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  margin-right: 4px;
}
.nav-avatar-btn {
  display: flex;
  align-items: center;
  gap: 2px;
  min-height: 44px;
  min-width: 44px;
  padding: 4px;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.nav-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e0f3fc;
  color: #0077a3;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.nav-avatar-chevron {
  color: #6b7280 !important;
}
.nav-avatar-menu {
  position: absolute;
  top: 42px;
  right: 0;
  width: 176px;
  background: #fff;
  border: 1px solid #e6e8ec;
  border-radius: 10px;
  z-index: 3;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.14);
}
.nav-avatar-who {
  padding: 8px 12px 6px;
  border-bottom: 1px solid #eef0f3;
}
.nav-avatar-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nav-avatar-title {
  margin-top: 1px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nav-avatar-menu button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #111827;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
}
.nav-avatar-menu button:hover {
  background: #e0f3fc;
}
.nav-avatar-signout {
  border-top: 1px solid #eef0f3 !important;
}
</style>
